import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Brain, Sparkles, Loader2, Copy, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';
const STAGES = ['Discovery', 'Pre-clinical', 'IND', 'Phase 1', 'Phase 2', 'Phase 3', 'Commercial'];
const PHASE_ORDER = { 'Commercial': 0, 'Phase 3': 1, 'Phase 2': 2, 'Phase 1': 3, 'Clinical Stage': 4, 'IND': 5, 'Pre-Clinical': 6 };
const SOURCE_OPTIONS = [
  { value: 'active', label: 'My Companies' },
  { value: 'database', label: 'Database' },
  { value: 'all', label: 'All' },
];

export default function PortfolioMatchPage() {
  const navigate = useNavigate();
  const [matchType, setMatchType] = useState('pharma');
  const [sourceFilter, setSourceFilter] = useState('active');
  const [stageFilter, setStageFilter] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [showKeywordOnly, setShowKeywordOnly] = useState(false);

  const sortByPhase = (rows) => [...rows].sort((a, b) => (PHASE_ORDER[a.phase] ?? 99) - (PHASE_ORDER[b.phase] ?? 99));
  // Sep 2026: when Claude rated fits, anti-tumour drugs that merely share
  // oncology keywords are collapsed by default — only Strong/Moderate shown.
  const aiRated = !!(result?.matches || []).some(m => m.fit_strength && m.fit_strength !== 'Keyword');
  const keywordOnlyRows = aiRated ? (result?.matches || []).filter(m => m.fit_strength === 'Keyword') : [];
  const visibleRows = !result ? [] : aiRated
    ? (result.matches || []).filter(m => showKeywordOnly || m.fit_strength !== 'Keyword')
    : ((result.matches || []).some(m => m.fit_strength) ? result.matches : sortByPhase(result.matches || []));

  const handleMatch = async () => {
    if (!query.trim()) { toast.error('Please enter the contact\'s interests'); return; }
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/ai/match-portfolio`, {
        interests: query,
        match_type: matchType,
        source_filter: sourceFilter,
        stage_filter: stageFilter,
      }, { withCredentials: true });
      setResult(res.data);
      const _rated = (res.data?.matches || []).some(m => m.fit_strength && m.fit_strength !== 'Keyword');
      const ids = new Set((res.data?.matches || []).filter(m => m.company_id && (!_rated || m.fit_strength !== 'Keyword')).map(m => m.company_id));
      setShowKeywordOnly(false);
      setSelected(ids);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to match portfolio');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (!result?.matches) return;
    const allIds = [...new Set(result.matches.filter(m => m.company_id).map(m => m.company_id))];
    if (selected.size >= allIds.length) setSelected(new Set());
    else setSelected(new Set(allIds));
  };

  const copyTable = () => {
    if (!result?.matches?.length) return;
    const rows = sortByPhase(result.matches);
    const isInv = matchType === 'investor';
    const headers = isInv
      ? ['Company','Molecule','Indication','Phase','Fundraising','Fit','Rationale']
      : ['Company','Molecule','Indication','Phase','Fit','Rationale'];
    const cellStyle = 'border:1px solid #cbd5e1;padding:8px;';
    const headStyle = 'border:1px solid #94a3b8;padding:8px;text-align:left;';
    const headerHtml = headers.map(h => `<th style="${headStyle}">${h}</th>`).join('');
    const html = `<table border="1" cellpadding="8" cellspacing="0" style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:13px;border:1px solid #94a3b8;">
<tr style="background:#f1f5f9;">${headerHtml}</tr>
${rows.map(r => {
  const cells = isInv
    ? [r.company || '', r.molecule || '', r.indication || '', r.phase || '', r.fundraising || '', r.fit_strength || 'Moderate', r.match_reason || '']
    : [r.company || '', r.molecule || '', r.indication || '', r.phase || '', r.fit_strength || 'Moderate', r.match_reason || ''];
  return `<tr>${cells.map(c => `<td style="${cellStyle}">${c}</td>`).join('')}</tr>`;
}).join('\n')}
</table>`;
    const plain = headers.join('\t') + '\n' + rows.map(r => {
      const cells = isInv
        ? [r.company || '', r.molecule || '', r.indication || '', r.phase || '', r.fundraising || '', r.fit_strength || 'Moderate', r.match_reason || '']
        : [r.company || '', r.molecule || '', r.indication || '', r.phase || '', r.fit_strength || 'Moderate', r.match_reason || ''];
      return cells.join('\t');
    }).join('\n');
    const blob = new Blob([html], { type: 'text/html' });
    const plainBlob = new Blob([plain], { type: 'text/plain' });
    navigator.clipboard.write([new ClipboardItem({ 'text/html': blob, 'text/plain': plainBlob })])
      .then(() => toast.success('Table copied — paste into email for formatted table'))
      .catch(() => navigator.clipboard.writeText(plain).then(() => toast.success('Table copied as text')));
  };

  const handleEmailSelected = () => {
    if (selected.size === 0) { toast.error('Select at least one company'); return; }
    // Build a per-company asset map from the matched rows so the bulk-email
    // backend can personalise [Asset]/[Indication]/[Modality]/[Stage] using
    // the EXACT asset Claude/the deterministic matcher surfaced — not a
    // generic "most-advanced asset" re-query that's locked to oncology.
    // When a company has multiple matching rows (e.g. two women's-health
    // assets), we keep the first row (already phase-sorted by sortByPhase).
    const matchedAssets = {};
    const sortedRows = sortByPhase(result?.matches || []);
    for (const row of sortedRows) {
      if (!row.company_id || !selected.has(row.company_id)) continue;
      if (matchedAssets[row.company_id]) continue;  // already have one for this co
      matchedAssets[row.company_id] = {
        asset_name: row.molecule || '',
        indication: row.indication || '',
        modality: row.modality || '',
        stage: row.phase || '',
        therapeutic_area: row.indication || '',
      };
    }
    try {
      sessionStorage.setItem('bulk_email_matched_assets', JSON.stringify({
        interests: query,
        matched_assets: matchedAssets,
      }));
    } catch { /* sessionStorage full or blocked — non-fatal, backend still works */ }
    const ids = Array.from(selected).join(',');
    navigate(`/biotech?ai_match_ids=${encodeURIComponent(ids)}`);
  };

  const accentBg = matchType === 'investor' ? 'bg-sky-50' : 'bg-violet-50';
  const accentText = matchType === 'investor' ? 'text-sky-600' : 'text-violet-600';
  const Icon = matchType === 'investor' ? Brain : Sparkles;

  return (
    <div className="p-6 max-w-7xl" data-testid="portfolio-match-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Portfolio Match</h1>
        <p className="text-sm text-slate-500 mt-1">
          Paste a pharma or investor's stated interests — Claude will surface matching assets from your portfolio.
        </p>
      </div>

      {/* Input Card */}
      <Card className="border border-slate-200 shadow-sm mb-6">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${accentBg}`}>
              <Icon className={`w-4 h-4 ${accentText}`} />
            </div>
            <div className="flex-1 min-w-0">
              {/* Type toggle */}
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <div className="flex bg-slate-100 rounded-md p-0.5" data-testid="match-type-toggle">
                  <button
                    type="button"
                    onClick={() => setMatchType('pharma')}
                    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${matchType === 'pharma' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    data-testid="match-type-pharma"
                  >Pharma</button>
                  <button
                    type="button"
                    onClick={() => setMatchType('investor')}
                    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${matchType === 'investor' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    data-testid="match-type-investor"
                  >Investor</button>
                </div>
                <span className="text-[10px] text-slate-400">Source:</span>
                <div className="flex bg-slate-100 rounded-md p-0.5" data-testid="source-toggle">
                  {SOURCE_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setSourceFilter(opt.value)}
                      className={`px-2.5 py-1 text-[11px] font-medium rounded transition-colors ${sourceFilter === opt.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                      data-testid={`source-${opt.value}`}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>

              {/* Stage filter pills */}
              <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="stage-filter">
                <span className="text-[10px] font-medium text-slate-500 mr-0.5">Stage:</span>
                {STAGES.map(s => {
                  const active = stageFilter.includes(s);
                  return (
                    <button key={s} type="button"
                      onClick={() => setStageFilter(prev => active ? prev.filter(x => x !== s) : [...prev, s])}
                      className={`text-[10px] px-2 py-1 rounded-md border transition-colors ${
                        active ? 'bg-[#00709B] text-white border-[#00709B]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#00709B]/40'
                      }`}
                      data-testid={`stage-${s.toLowerCase().replace(/\s+/g, '-')}`}
                    >{s}</button>
                  );
                })}
                {stageFilter.length > 0 && (
                  <button type="button" onClick={() => setStageFilter([])}
                    className="text-[9px] px-1.5 py-0.5 text-slate-400 hover:text-slate-600 underline"
                    data-testid="clear-stage">Clear</button>
                )}
              </div>

              {/* Input + Match button */}
              <div className="flex gap-2">
                <Textarea
                  className="flex-1 text-sm min-h-[60px] max-h-[160px] resize-y"
                  placeholder={matchType === 'investor'
                    ? 'e.g., We invest in Series A/B oncology, rare disease, gene therapy, clinical-stage only...'
                    : 'e.g., Immunology, hematology, nephrology, commercial vaccines, immunoglobulins...'}
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleMatch(); } }}
                  data-testid="match-input"
                />
                <Button
                  className={`shrink-0 self-end text-white ${matchType === 'investor' ? 'bg-sky-600 hover:bg-sky-700' : 'bg-violet-600 hover:bg-violet-700'}`}
                  onClick={handleMatch}
                  disabled={loading || !query.trim()}
                  data-testid="match-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Icon className="w-4 h-4 mr-1.5" />}
                  Match
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
          <p className="text-sm text-slate-500">Analyzing portfolio against interests...</p>
        </div>
      )}

      {!loading && result?.matches?.length > 0 && (
        <Card className="border border-slate-200 shadow-sm" data-testid="match-results-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-xs text-slate-500">
                {aiRated ? `${visibleRows.length} rated fit${visibleRows.length !== 1 ? 's' : ''}` : `${result.matches.length} matching asset${result.matches.length !== 1 ? 's' : ''} found`}
                {result.total_assets_matched
                  ? ` (${result.total_assets_matched} assets from ${result.total_portfolio} companies matched by therapeutic area & stage)`
                  : ` from ${result.total_portfolio} total portfolio companies`}
                {typeof result.ai_ranked === 'number' && matchType === 'pharma' && (
                  <span className="ml-1" data-testid="ai-ranked-note">
                    · Claude rated <b>{result.ai_ranked}</b> as Strong/Moderate fits (listed first); the rest are keyword matches
                  </span>
                )}
                {result.prefilter_relaxed && <span className="ml-1 text-amber-600" data-testid="prefilter-relaxed-note">· no keyword overlap — showing the stage-eligible portfolio</span>}
                {keywordOnlyRows.length > 0 && (
                  <button type="button" onClick={() => setShowKeywordOnly(v => !v)} className="ml-2 text-violet-700 hover:underline" data-testid="toggle-keyword-only">
                    {showKeywordOnly ? `Hide ${keywordOnlyRows.length} keyword-only matches` : `Show ${keywordOnlyRows.length} keyword-only matches (not rated as fits)`}
                  </button>
                )}
              </p>
              <p className="text-xs text-slate-400">{selected.size} compan{selected.size === 1 ? 'y' : 'ies'} selected</p>
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="match-table">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-2 py-2.5 w-8">
                        <input type="checkbox" className="rounded border-slate-300"
                          checked={selected.size > 0 && selected.size >= new Set(result.matches.filter(m => m.company_id).map(m => m.company_id)).size}
                          onChange={toggleSelectAll}
                          data-testid="select-all" />
                      </th>
                      <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Company</th>
                      <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Molecule</th>
                      <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Indication</th>
                      <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Phase</th>
                      {matchType === 'investor' && <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Fundraising</th>}
                      <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Looking For</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {visibleRows.map((row, i) => (
                      <tr key={i} className={`hover:bg-slate-50/50 ${row.company_id && selected.has(row.company_id) ? 'bg-violet-50/30' : ''}`} data-testid={`match-row-${i}`}>
                        <td className="px-2 py-2.5">
                          {row.company_id ? (
                            <input type="checkbox" className="rounded border-slate-300"
                              checked={selected.has(row.company_id)}
                              onChange={() => toggleSelect(row.company_id)}
                              data-testid={`row-check-${i}`} />
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-2.5 text-xs font-semibold text-slate-800">{row.company || '—'}</td>
                        <td className="px-3 py-2.5 text-xs font-medium text-slate-900">{row.molecule}</td>
                        <td className="px-3 py-2.5 text-xs text-slate-600">{row.indication}</td>
                        <td className="px-3 py-2.5"><Badge variant="secondary" className="text-[10px]">{row.phase}</Badge></td>
                        {matchType === 'investor' && <td className="px-3 py-2.5 text-xs text-slate-600">{row.fundraising || '—'}</td>}
                        <td className="px-3 py-2.5 text-xs text-slate-500 max-w-[300px]">
                          {row.fit_strength && (
                            <Badge className={`text-[10px] mr-1.5 border ${row.fit_strength === 'Strong' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : row.fit_strength === 'Moderate' ? 'bg-sky-50 text-sky-700 border-sky-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`} data-testid={`fit-${i}`}>
                              {row.fit_strength === 'Keyword' ? 'Keyword match' : `${row.fit_strength} fit`}
                            </Badge>
                          )}
                          {row.match_reason || row.looking_for || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 mt-4">
              <Button variant="outline" className="text-violet-600 border-violet-200 hover:bg-violet-50"
                onClick={copyTable} data-testid="copy-results">
                <Copy className="w-4 h-4 mr-2" /> Copy Table
              </Button>
              <Button className="bg-[#00709B] text-white hover:bg-[#005a7a]"
                disabled={selected.size === 0}
                onClick={handleEmailSelected}
                data-testid="email-selected">
                <Mail className="w-4 h-4 mr-2" /> Email {selected.size > 0 ? `${selected.size} Companies` : 'Selected'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && result?.raw_text && (
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <pre className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" style={{ fontFamily: 'Public Sans, sans-serif' }}>{result.raw_text}</pre>
            <div className="flex justify-end mt-4">
              <Button variant="outline" className="text-violet-600 border-violet-200 hover:bg-violet-50"
                onClick={() => { navigator.clipboard.writeText(result.raw_text); toast.success('Copied to clipboard'); }}
                data-testid="copy-raw">
                <Copy className="w-4 h-4 mr-2" /> Copy Results
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && result && result.matches?.length === 0 && !result.raw_text && (
        <div className="text-center py-12">
          <Brain className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No matching assets found for these interests.</p>
        </div>
      )}
    </div>
  );
}
